import * as React from 'react'
import { useForm } from '@tanstack/react-form'
import { toast } from 'sonner'
import { Plus, Minus } from 'lucide-react'

import type { TaxRate } from '@/gen/cortado/v1/invoices_pb'
import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { formatCurrency, formatTaxRate, parseCurrency } from '@/lib/currency-utils'
import { formatEnumName } from '@/lib/enum-utils'
import { useSystemSettings } from '@/stores/settings.store'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { InputGroupInput } from '@/components/ui/input-group'
import { Label } from '@/components/ui/label'
import { RichTextEditor } from '@/components/shared/rich-text-editor'
import { useRichTextEditor } from '@/hooks/use-rich-text-editor'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import type { SheetLayout } from '@/components/shared/sheet-layout'
import { cn } from '@/lib/utils'

import type { QuoteAddonOfferFormValue } from './quote-addon-offers-table'

const MIN_ADDON_QUANTITY = 0.01

function UnitPriceInput({
  value,
  onChange,
  disabled,
}: {
  value: number
  onChange: (cents: number) => void
  disabled?: boolean
}) {
  const [displayValue, setDisplayValue] = React.useState(() => formatCurrency(value))
  const internalCents = React.useRef(value)

  // Only sync display when value changes externally (e.g., form reset), not from our own onChange
  React.useEffect(() => {
    if (value !== internalCents.current) {
      setDisplayValue(formatCurrency(value))
      internalCents.current = value
    }
  }, [value])

  return (
    <div className="grid gap-3">
      <Label htmlFor="addon-unit-price">
        Price <span className="text-destructive">*</span>
      </Label>
      <div className="flex items-center">
        <span className="text-sm text-muted-foreground mr-2">$</span>
        <Input
          id="addon-unit-price"
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={(e) => {
            setDisplayValue(e.target.value)
            const cents = parseCurrency(e.target.value)
            internalCents.current = cents
            onChange(cents)
          }}
          onBlur={() => {
            const cents = parseCurrency(displayValue)
            internalCents.current = cents
            setDisplayValue(formatCurrency(cents))
          }}
          placeholder="0.00"
          disabled={disabled}
          required
        />
      </div>
    </div>
  )
}

interface QuoteAddonOfferEditSheetProps {
  offer: QuoteAddonOfferFormValue | null
  availableTaxRates: TaxRate[]
  open: boolean
  onOpenChange: (open: boolean) => void
  onSave: (offer: QuoteAddonOfferFormValue) => void
  disabled?: boolean
  /** 'pipeline' applies the shared pipeline sheet width and footer layout. */
  layout?: SheetLayout
}

export function QuoteAddonOfferEditSheet({
  offer,
  availableTaxRates,
  open,
  onOpenChange,
  onSave,
  disabled = false,
  layout,
}: QuoteAddonOfferEditSheetProps) {
  const { settings } = useSystemSettings()
  const itemUnits = settings?.itemUnits || []
  const availableUnits = itemUnits.filter(
    (unit) => unit !== ItemUnitEnum.ITEM_UNIT_UNKNOWN && unit !== ItemUnitEnum.ITEM_UNIT_PER_MILE,
  )
  const isCreateMode = !offer?.id && !offer?.tempId && !offer?.sourceItemId
  const isCustomOffer = !offer?.sourceItemId

  // Setup form with default values
  const form = useForm({
    defaultValues: {
      name: offer?.name ?? '',
      description: offer?.description ?? '',
      unitPriceCents: offer?.unitPriceCents ?? 0,
      quantity: offer?.quantity ?? 1,
      unit: offer?.unit ?? ItemUnitEnum.ITEM_UNIT_PER_EVENT,
      isTaxable: offer?.isTaxable ?? false,
      salesTaxRateId: offer?.salesTaxRateId,
      salesTaxRateName: offer?.salesTaxRateName,
      salesTaxRate: offer?.salesTaxRate,
    },
    onSubmit: async ({ value }) => {
      if (!value.name.trim()) {
        toast.error('Add-on name is required')
        return
      }

      if (value.unitPriceCents < 0) {
        toast.error('Price cannot be negative')
        return
      }

      if (value.quantity <= 0) {
        toast.error('Quantity must be greater than 0')
        return
      }

      const hasSalesTaxRate = Boolean(value.salesTaxRateId && value.salesTaxRate)
      const updatedOffer: QuoteAddonOfferFormValue = {
        ...offer,
        id: offer?.id,
        sourceItemId: offer?.sourceItemId,
        tempId: offer?.tempId || (isCreateMode ? crypto.randomUUID() : undefined),
        name: value.name,
        description: value.description,
        unitPriceCents: value.unitPriceCents,
        unit: value.unit,
        quantity: value.quantity,
        sortOrder: offer?.sortOrder ?? 0,
        isTaxable: hasSalesTaxRate,
        salesTaxRateId: hasSalesTaxRate ? value.salesTaxRateId : undefined,
        salesTaxRateName: hasSalesTaxRate ? value.salesTaxRateName : undefined,
        salesTaxRate: hasSalesTaxRate ? value.salesTaxRate : undefined,
      }

      onSave(updatedOffer)
      toast.success(isCreateMode ? 'Add-on added successfully' : 'Add-on updated successfully')
      onOpenChange(false)
      form.reset()
    },
  })

  const descriptionEditor = useRichTextEditor({
    initialContent: offer?.description ?? '',
    placeholder: 'Enter add-on description (optional)',
    onUpdate: (html) => {
      form.setFieldValue('description', html)
    },
  })

  React.useEffect(() => {
    descriptionEditor?.setEditable(!disabled)
  }, [descriptionEditor, disabled])

  // Reset form when offer changes
  React.useEffect(() => {
    if (offer) {
      form.reset({
        name: offer.name ?? '',
        description: offer.description ?? '',
        unitPriceCents: offer.unitPriceCents ?? 0,
        quantity: offer.quantity ?? 1,
        unit: offer.unit ?? ItemUnitEnum.ITEM_UNIT_PER_EVENT,
        isTaxable: offer.isTaxable ?? false,
        salesTaxRateId: offer.salesTaxRateId,
        salesTaxRateName: offer.salesTaxRateName,
        salesTaxRate: offer.salesTaxRate,
      })
      descriptionEditor?.commands.setContent(offer.description ?? '')
    }
  }, [offer, form, descriptionEditor])

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn(layout === 'pipeline' ? 'w-full sm:max-w-xl' : 'w-full sm:max-w-lg')}>
        <SheetHeader>
          <SheetTitle>{isCreateMode ? 'Add Custom Add-on' : 'Edit Add-on'}</SheetTitle>
          <SheetDescription>
            {isCreateMode
              ? 'Create a one-off add-on for this quote.'
              : 'Override the price or description for this add-on. Click save when you\'re done.'}
          </SheetDescription>
        </SheetHeader>
        <SheetBody>
          <form
            id="quote-addon-offer-form"
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              form.handleSubmit()
            }}
            className="grid auto-rows-min gap-6"
          >
            <form.Field name="name">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor={isCustomOffer ? 'addon-name' : undefined}>
                    Add-on Name
                    {isCustomOffer && <span className="text-destructive"> *</span>}
                  </Label>
                  {isCustomOffer ? (
                    <Input
                      id="addon-name"
                      value={field.state.value}
                      onChange={(event) => field.handleChange(event.target.value)}
                      placeholder="Enter add-on name"
                      disabled={disabled}
                      required
                    />
                  ) : (
                    <p className="text-sm font-medium">{offer?.name}</p>
                  )}
                </div>
              )}
            </form.Field>

            {/* Description */}
            <div className="grid gap-3">
              <Label htmlFor="addon-description">Description</Label>
              <RichTextEditor editor={descriptionEditor} disabled={disabled} />
            </div>

            {/* Unit Price */}
            <form.Field name="unitPriceCents">
              {(field) => (
                <UnitPriceInput
                  value={field.state.value}
                  onChange={(cents) => field.handleChange(cents)}
                  disabled={disabled}
                />
              )}
            </form.Field>

            {/* Quantity and Unit */}
            <div className="grid grid-cols-2 gap-4">
              <form.Field name="quantity">
                {(field) => (
                  <div className="grid gap-3">
                    <Label htmlFor="addon-quantity">
                      Quantity <span className="text-destructive">*</span>
                    </Label>
                    <div className="flex items-center rounded-md border border-input shadow-xs overflow-hidden">
                      <div className="flex border-r border-input">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const newValue = Math.max(MIN_ADDON_QUANTITY, field.state.value - 1)
                            field.handleChange(newValue)
                          }}
                          disabled={disabled || field.state.value <= MIN_ADDON_QUANTITY}
                        >
                          <Minus className="h-4 w-4" />
                          <span className="sr-only">Decrease</span>
                        </Button>
                      </div>
                      <InputGroupInput
                        id="addon-quantity"
                        type="number"
                        value={field.state.value}
                        onChange={(e) => {
                          const value = Number.parseFloat(e.target.value)
                          if (Number.isFinite(value)) {
                            field.handleChange(value)
                          }
                        }}
                        onBlur={(e) => {
                          const value = Number.parseFloat(e.target.value)
                          field.handleChange(Number.isFinite(value) && value > 0 ? value : 1)
                        }}
                        min={MIN_ADDON_QUANTITY}
                        step="any"
                        disabled={disabled}
                        className="text-center text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <div className="flex border-l border-input">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            field.handleChange(field.state.value + 1)
                          }}
                          disabled={disabled}
                        >
                          <Plus className="h-4 w-4" />
                          <span className="sr-only">Increase</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </form.Field>

              <form.Field name="unit">
                {(field) => (
                  <div className="grid gap-3">
                    <Label htmlFor="addon-unit">
                      Unit <span className="text-destructive">*</span>
                    </Label>
                    <Select
                      value={field.state.value.toString()}
                      onValueChange={(value) => field.handleChange(parseInt(value) as ItemUnitEnum)}
                      disabled={disabled}
                    >
                      <SelectTrigger id="addon-unit">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {availableUnits.map((unit) => (
                          <SelectItem key={unit} value={unit.toString()}>
                            {formatEnumName(unit, ItemUnitEnum)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>

            <form.Field name="salesTaxRateId">
              {(field) => (
                <div className="grid gap-3">
                  <Label htmlFor="addon-tax-rate">Sales Tax Rate</Label>
                  <Select
                    value={field.state.value || 'no-tax'}
                    onValueChange={(value) => {
                      if (value === 'no-tax') {
                        field.handleChange(undefined)
                        form.setFieldValue('isTaxable', false)
                        form.setFieldValue('salesTaxRateName', undefined)
                        form.setFieldValue('salesTaxRate', undefined)
                        return
                      }

                      const selectedRate = availableTaxRates.find((rate) => rate.id === value)
                      field.handleChange(value)
                      form.setFieldValue('isTaxable', true)
                      form.setFieldValue('salesTaxRateName', selectedRate?.name)
                      form.setFieldValue('salesTaxRate', selectedRate ? selectedRate.percentage / 100 : undefined)
                    }}
                    disabled={disabled}
                  >
                    <SelectTrigger id="addon-tax-rate">
                      <SelectValue placeholder="Select tax rate" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-tax">No Tax</SelectItem>
                      {availableTaxRates.map((rate) => (
                        <SelectItem key={rate.id} value={rate.id}>
                          {rate.name} ({formatTaxRate(rate.percentage)}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter className={cn(layout === 'pipeline' && 'sm:justify-between')}>
          <SheetClose asChild>
            <Button type="button" variant="outline" disabled={disabled}>
              Cancel
            </Button>
          </SheetClose>
          <form.Subscribe selector={(s) => [s.values.name, s.values.unitPriceCents, s.values.quantity, s.isSubmitting] as const}>
            {([name, unitPriceCents, quantity, isSubmitting]) => (
              <Button
                type="submit"
                form="quote-addon-offer-form"
                disabled={disabled || isSubmitting || !name.trim() || unitPriceCents < 0 || quantity <= 0}
              >
                {disabled ? 'Saving...' : isCreateMode ? 'Add Add-on' : 'Save Changes'}
              </Button>
            )}
          </form.Subscribe>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
